import autobind from "autobind-decorator";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import _ from "lodash";
import { flushSync } from "react-dom";
import { connect } from "react-redux";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import type { ISidebarListItem } from "@similarweb/ui-components";
import { WeeklyCalendarLocked } from "@similarweb/ui-components";

import { durationFilterDSSelected, webSourceFilterDSSelected } from "app/actions/usersFiltersSelectionActions";
import { hideCountryFilterHof } from "app/actions/WebsiteAnalysisFilterActions";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { preparePresets } from "app/components/dashboard/widget-wizard/components/DashboardWizardDuration";
import { PeriodOverPeriodWrapper } from "app/components/duration-selector/period-over-period/PeriodOverPeriodWrapper";
import type { ESortingType } from "app/components/filters-bar/country-filter/CountryFilterTypes";
import GaFilter from "app/components/filters-bar/ga-filter/GaFilter";
import { SubDomainsFilter } from "app/components/filters-bar/subdomains-filter/SubDomainsFilter";
import * as utils from "app/components/filters-bar/utils";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { WebSourceFilter } from "app/components/filters-bar/websource-filter/WebSourceFilter";
import type { IFilter } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersBar } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersMenu } from "app/components/React/FiltersBar/FiltersMenu";
import { withUIRouter } from "app/components/React/HOCs/withUIRouter";
import { translate } from "app/filters/i18nFilter";
import { isWeeklyKeywordsAvailable } from "app/pages/keyword-analysis/common/UtilityFunctions";
import { WEBSITE_ANALYSIS_PERFORMANCE } from "app/pages/sales-intelligence/constants/routes";
import { DurationSelectorSimple } from "app/components/duration-selector/DurationSelectorSimple";
import { FixedDurationsSelector } from "app/components/duration-selector/FixedDurationsSelector";
import { updateFilterConsistencyCountry } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import DurationService from "app/services/DurationService";
import { openDefaultHooksModal, openHooksModalV5 } from "app/services/ModalService";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { DurationSelectorPresetOriented } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented";
import { IndicatorTypes } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import type { IndicatorType } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { FilterChangedNotification } from "pro-features/components/tooltips/src/DMIFilterTooltip/FilterEducationTooltip";
import type { IFilterChangedNotificationProps } from "pro-features/components/tooltips/src/DMIFilterTooltip/FilterEducationTooltip";
import { EFilterChangedTooltipTypes } from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IUnlockModalConfigTypes } from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import { estimatedVsGaSwitchToggle, toggleResetBetaBranchModal } from "../../actions/commonActions";
import { CountryFilter } from "../../components/filters-bar/country-filter/CountryFilter";
import type { IFiltersBaseState } from "../../components/filters-bar/FiltersBase";
import { FiltersBase } from "../../components/filters-bar/FiltersBase";
import { connectedAssetsService } from "../../services/ConnectedAssetsService";
import { allTrackers } from "../../services/track/track";
import { EHooksVersions } from "app/services/SwHook";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";

const StyledSwitcherItem = styled(FlexRow)`
    display: inline-flex;
    font-weight: 500;

    ${SWReactIcons} {
        display: flex;
        align-items: center;
        margin-left: 8px;

        svg {
            path {
                fill: ${colorsPalettes.indigo[300]};
            }
        }
    }
`;

export const getMaxSelectableDurationRangeInfoMsg = () => translate("duration.selector.max.range.info.msg");
export const getMaxSelectableDurationRangeWarningMsg = () => translate("duration.selector.max.range.error.msg");

const DAILY_DATA_INCLUDE_SUBDOMAINS_FROM = dayjs.utc("2020/05/29");
const worldwideOverviewArray = [
    "accountreview_website_overview_websiteperformance",
    WEBSITE_ANALYSIS_PERFORMANCE,
    "digitalsuite_website_websiteperformance",
];

interface IAppStateParams {
    /* Countries */
    availableCountries: any[];
    selectedCountry: any;
    /* Websources */
    availableWebsource: any[];
    selectedWebSource: any;
    /* Duration */
    minDate: Dayjs;
    maxDate: Dayjs;
    minDailyDate: Dayjs;
    maxDailyDate: Dayjs;
    selectedDuration: string;
    selectedComparedDuration: string;
    durationSelectorPresets: any;
    durationSelectorWeeklyPresets: any;
    /* Include subdomains */
    includeSubDomains: any;
    isSubDomainsFilterDisabled: boolean;
    includeSubDomainsDisabledTooltip?: string;
    comparedDurationItems: boolean;
    isCountryFilterAllowData?: boolean;
    countryFilterSortingText?: string;
    countryFilterInitialSortingType?: ESortingType;
    countryFilterPreferencesKey?: string;
    isUSStatesSupported?: boolean;
}

interface IWebsiteAnalysisFiltersState extends IAppStateParams, IFiltersBaseState {
    params: any;
    componentName: string;
    keys: string;
    showInvalidDurationTooltip: boolean;
    invalidDurationTooltipMessage: string;
    periodOverPeriodDisabled: boolean;
    countryFilterDisabled: boolean;
    countryFilterAvailable: boolean;
    durationSelectorDisabled: boolean;
    durationSelectorAvailable: boolean;
    webSourceFilterAvailable: boolean;
    webSourceFilterDisabled: boolean;
    compareToPlaceholder: string;
    comparedDurationAllowed: boolean;
    isShowEstimationsVsGaToggle: boolean;
    durationPaymentHook: boolean;
    mainWebsiteIsSubDomain: boolean;
    showBetaBranchData?: { isUpdating: boolean; value: boolean };
    isCountryHookOpen?: boolean;
    openResetBetaBranchModal?: () => void;
    resolveCompareDurationAllowed?: () => void;
    TooltipChangeProps?: IFilterChangedNotificationProps;
    skipFilterConsistency?: boolean;
    customData: any;
}

const hideTooltipDelay = 10000;

class WebsiteAnalysisFilters extends FiltersBase<any, IWebsiteAnalysisFiltersState> {
    private stateChangeSuccessHandler;
    private stateChangeStartHandler;
    private navUpdateHandler;

    constructor(props) {
        super(props);
        this.services = {
            ...this.services,
            periodOverPeriod: periodOverPeriodService,
        };

        const params = this.services.swNavigator.getParams();
        const state = this.services.swNavigator.current();

        const TooltipChangeProps = {
            preferencesKey: state.customData?.countryFilterPreferencesKey,
            onTooltipVisible: () =>
                PreferencesService.add({
                    [this.state.customData?.countryFilterPreferencesKey]: true,
                }),
            firstTooltipTitle: translate("dmi.country.filter.change.first.title"),
            tooltipTitle: translate("dmi.country.filter.change.title"),
            firstTooltipBody: translate("dmi.country.filter.change.first.body"),
            tooltipBody: translate("dmi.country.filter.change.body"),
            filterType: EFilterChangedTooltipTypes.COUNTRY_PICKER,
        };
        const stateParams = this.getStateParams(params, state);
        const periodOverPeriodDisabled = this.isPeriodOverPeriodDisabled(stateParams.comparedDurationItems);
        const countryFilterDisabled = this.isCountryDropDownDisabled(state);
        const countryFilterAvailable = this.isCountryFilterAvailable(state);
        const durationSelectorAvailable = this.isDurationSelectorAvailable(state);
        const durationSelectorDisabled = this.isDurationSelectorDisabled(params, state);
        const webSourceFilterAvailable = this.isWebSourceFilterAvailable(state);
        const webSourceFilterDisabled = this.isWebSourceFilterDisabled(params, state);
        this.state = {
            params,
            isSideBarOpen: false,
            componentName: this.services.swSettings.current.componentId,
            keys: params.key,
            compareToPlaceholder: translate("compared.duration.dropdown.empty"),
            showInvalidDurationTooltip: false,
            invalidDurationTooltipMessage: "",
            ...stateParams,
            periodOverPeriodDisabled,
            countryFilterDisabled,
            countryFilterAvailable,
            durationSelectorDisabled,
            durationSelectorAvailable,
            webSourceFilterAvailable,
            webSourceFilterDisabled,
            comparedDurationAllowed: this.resolveCompareDurationAllowed(params, state),
            isShowEstimationsVsGaToggle: false,
            durationPaymentHook: this.services.swSettings.current.durationPaymentHook,
            mainWebsiteIsSubDomain: chosenSites.getPrimarySite().isSubDomain,
            TooltipChangeProps,
            skipFilterConsistency: state.skipFilterConsistency,
            customData: state.customData,
        };

        this.updateGAToggleState(params.key);
    }

    public componentDidMount() {
        const router = this.props.router;
        this.stateChangeSuccessHandler = router.transitionService.onSuccess({}, (transition) =>
            this.onStateChangeSuccess(null, transition.to(), transition.params()),
        );
        this.stateChangeStartHandler = router.transitionService.onStart({}, (transition) => {
            const toState = transition.to();
            const toParams = transition.params();
            const fromState = transition.from();
            const fromParams = transition.params("from");
            this.onStateChangeStart(
                null,
                toState,
                toParams,
                fromState,
                fromParams,
                proNavigator.getConfigIdFromStateConfig(toState, toParams),
            );
        });
        this.navUpdateHandler = this.props.router.transitionService.onSuccess({}, this.onNavUpdate);
    }

    public componentWillUnmount() {
        this.stateChangeSuccessHandler();
        this.stateChangeStartHandler();
        if (typeof this.navUpdateHandler === "function") {
            this.navUpdateHandler();
        }
    }

    public componentDidUpdate(prevProps, prevState) {
        if (prevState.showInvalidDurationTooltip === true) {
            setTimeout(() => {
                this.setState({
                    ...this.state,
                    showInvalidDurationTooltip: false,
                    invalidDurationTooltipMessage: "",
                });
            }, hideTooltipDelay);
        }
    }

    public render() {
        return (
            <div className="react-filters-container">
                <FiltersBar filters={this.getFilters()} />
                {this.getCompactFilters().length > 0 ? (
                    <FiltersMenu
                        compactFilters={this.getCompactFilters()}
                        isSideBarOpen={this.state.isSideBarOpen}
                        toggleSidebar={this.toggleSidebar}
                        onSidebarToggle={this.onSideBarToggle}
                    />
                ) : null}
            </div>
        );
    }

    @autobind
    public onSortChange(sortingType = null) {
        this.setState({
            availableCountries: utils.getCountries(false, swSettings.current, sortingType),
        });
    }

    @autobind
    private getFilters(): IFilter[] {
        const selectedCountryId = utils.getSelectedId(this.state.selectedCountry);
        const selectedWebSource = utils.getSelectedId(this.state.selectedWebSource);
        const showDurationFilter = this.state.durationSelectorAvailable;
        const showCountryFilter = this.state.countryFilterAvailable;
        const showWebSourceFilter = this.state.availableWebsource.length > 0 && this.state.webSourceFilterAvailable;
        const isCountryChanged = !!proNavigator.getParams()?.isCountryChanged;
        const filters = [];
        if (showCountryFilter) {
            filters.push({
                id: "country",
                key: "country",
                filter: (
                    <div key="filter-country">
                        <CountryFilter
                            countryFilterInitialSortingType={this.state.customData?.countryFilterInitialSortingType}
                            selectedCountry={proNavigator.getParams().country}
                            selectedDomain={proNavigator.getParams().selectedDomain}
                            sortChange={this.onSortChange}
                            isCountryFilterAllowData={this.state.customData?.isCountryFilterAllowData}
                            countryFilterSortingText={this.state.customData?.countryFilterSortingText}
                            availableCountries={this.state.availableCountries}
                            presentNotificationIndicator={isCountryChanged}
                            presentNotificationTooltipKey={"website.analysis.present.country.notification.tooltip"}
                            isUSStatesSupported={this.state.isUSStatesSupported}
                            changeCountry={this.onCountryChange}
                            selectedCountryIds={selectedCountryId}
                            height={60}
                            appendTo="body"
                            isOpenRequested={this.props.isCountryFilterOpen}
                            onOpenRequestConsumed={this.props.closeCountryFilter}
                            disabled={this.state.countryFilterDisabled || this.props.isDisabled}
                            showUpgradeHook={
                                isNoTouch()
                                    ? !this.services.swNavigator.current().data.disableNoTouchForCountryFilter
                                    : null
                            }
                            onToggle={this.onCountryFilterToggle}
                            itemWrapper={(props) => (
                                <div
                                    onClick={async () =>
                                        await handleCTAClick(
                                            EHooksVersions.V4,
                                            "WebCountry",
                                            TestCases.TRIAL_AFTER_TRIAL,
                                        )
                                    }
                                >
                                    {props.children}
                                </div>
                            )}
                        />
                    </div>
                ),
            });
        }

        if (showDurationFilter) {
            filters.unshift({
                id: "duration",
                key: "duration",
                filter: this.getDurationSelectorComponent(this.state.showInvalidDurationTooltip),
            });
        }

        if (showWebSourceFilter && selectedWebSource) {
            filters.push({
                id: "webSource",
                key: "webSource",
                filter: this.getWebSourceFilterComponent(selectedWebSource),
            });
        }
        return filters;
    }
    @autobind
    private getWebSourceFilterComponent(selectedWebSource) {
        const activeSlide: IUnlockModalConfigTypes["DeviceFilters"] = "Devices";
        const isWebSourceChanged = !!proNavigator.getParams()?.isWebSourceChanged;
        const component = (
            <div key="filter-websource">
                <WebSourceFilter
                    items={this.state.availableWebsource}
                    onChange={this.onWebSourceChange}
                    selectedIds={selectedWebSource}
                    height={60}
                    appendTo="body"
                    disabled={
                        this.state.webSourceFilterDisabled ||
                        this.state.availableWebsource.length == 1 ||
                        this.props.isDisabled
                    }
                    onToggle={this.trackFilterDropDownStatus("Header/WebSource Filter")}
                    presentNotificationIndicator={isWebSourceChanged}
                    presentNotificationTooltipKey={"website.analysis.present.webSource.notification.tooltip"}
                    itemWrapper={(props) => (
                        <div
                            onClick={() => {
                                openDefaultHooksModal(
                                    "Web Source Filter",
                                    LocationService.getCurrentLocationTracking(),
                                );
                                allTrackers.trackEvent(
                                    "hook/Contact Us/Pop Up",
                                    "click",
                                    `WebSource Filter/${props.text}`,
                                );
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                />
            </div>
        );
        return component;
    }

    @autobind
    private getDurationSelectorComponent(wrapWithTooltip = false) {
        const DURATION_12_MONTH = "12m";
        const DURATION_15_MONTH = "15m";
        const durationsIsAddonAvailable = [DURATION_12_MONTH, DURATION_15_MONTH];
        const current = this.services.swNavigator.current();
        const params = this.services.swNavigator.getParams();
        const { webSource, isDurationChanged } = params;
        const isNewMMXAlgoProps =
            webSource !== devicesTypes.DESKTOP &&
            (current.name === "digitalsuite_website_marketingchannels" ||
                current.name === "accountreview_website_marketingchannels");

        const compareDisabledTooltipText = isNewMMXAlgoProps
            ? translate("websiteanalysis.duration.compare.disabled.tooltip.new.mmx.algo")
            : this.state.keys?.indexOf(",") > -1
              ? translate("websiteanalysis.duration.compare.disabled.tooltip.multiple_websites")
              : translate("websiteanalysis.duration.compare.disabled.tooltip");

        const isFixedSnapshotRangeLength = typeof swSettings.current.resources.FixedSnapshotRangeLength === "number";

        const ComponentClass = isFixedSnapshotRangeLength ? FixedDurationsSelector : DurationSelectorSimple;
        let { hideCalendar } = current;
        // set hideCalendar to true in case the current component uses FixedSnapshotRangeLength.
        // We do that in order to "apply" the changes immediately after preset selection
        if (isFixedSnapshotRangeLength) {
            hideCalendar = true;
        }
        const presentNotificationForDurationChanged = isDurationChanged === true;
        const isAddOnExist = (): boolean => {
            const isDurationsExists =
                this.state.durationSelectorPresets || Array.isArray(this.state.durationSelectorPresets);

            if (!isDurationsExists) {
                return false;
            }

            const isAddOnExist = this.state.durationSelectorPresets.some((preset) =>
                durationsIsAddonAvailable.includes(preset.value),
            );

            return isAddOnExist;
        };
        const getDurationSelectorTooltipKey = (): string => {
            if (presentNotificationForDurationChanged) {
                return "website.analysis.present.duration.notification.tooltip";
            }

            return "";
        };

        const getIndicatorType = (): IndicatorType => {
            if (presentNotificationForDurationChanged) {
                return IndicatorTypes.DOT;
            }

            if (isAddOnExist()) {
                return IndicatorTypes.PULSE;
            }

            return IndicatorTypes.NONE;
        };

        const isDurationSelectorIndicatorShown = presentNotificationForDurationChanged || isAddOnExist();

        const maxDurationRange = {
            maxSelectableDurationRangeNumber: swSettings.current.resources.MaxAllowedIntervalRange,
            maxSelectableDurationRangeWarningMsg: getMaxSelectableDurationRangeWarningMsg(),
            maxSelectableDurationRangeInfoMsg: getMaxSelectableDurationRangeInfoMsg(),
        };
        const component =
            isFixedSnapshotRangeLength || !this.services.swSettings.user.isShortMonthIntervalsUser ? (
                <ComponentClass
                    key="filter-duration"
                    minDate={this.state.minDate}
                    maxDate={this.state.maxDate}
                    minDailyDate={this.state.minDailyDate}
                    maxDailyDate={this.state.maxDailyDate}
                    isDisabled={this.state.durationSelectorDisabled || this.props.isDisabled}
                    presets={preparePresets(this.state.durationSelectorPresets)}
                    weeklyPresets={preparePresets(this.state.durationSelectorWeeklyPresets)}
                    initialPreset={this.state.selectedDuration}
                    initialComparedDuration={this.state.selectedComparedDuration || false}
                    onSubmit={this.onDurationChange}
                    componentName={this.state.componentName}
                    keys={this.state.keys}
                    compareAllowed={this.state.comparedDurationAllowed}
                    compareLabel={translate("websiteanalysis.duration.compare.label")}
                    compareDisabledTooltipText={compareDisabledTooltipText}
                    hasPermissionsLock={this.state.durationPaymentHook}
                    onToggle={this.trackFilterDropDownStatus("Date range")}
                    disableCalendar={hideCalendar}
                    disableFooter={hideCalendar}
                    appendTo="body"
                    showWeeklyToggle={this.services.swSettings.current.resources.WeeklyGranularity === true}
                    isWeeklyDataAvailable={isWeeklyKeywordsAvailable(this.services.swSettings)}
                    weeklyCalendarLocked={this.getWeeklyCalendarLocked()}
                    isShowPoPDropdownLockModal={!isNewMMXAlgoProps}
                    presentNotificationIndicator={presentNotificationForDurationChanged}
                    presentNotificationTooltipKey={"website.analysis.present.duration.notification.tooltip"}
                    maxDurationRange={maxDurationRange.maxSelectableDurationRangeNumber ? maxDurationRange : null}
                />
            ) : (
                <DurationSelectorPresetOriented
                    minDate={this.state.minDate}
                    maxDate={this.state.maxDate}
                    isDisabled={this.state.durationSelectorDisabled || this.props.isDisabled}
                    onSubmit={this.onDurationChange}
                    presets={preparePresets(this.state.durationSelectorPresets)}
                    initialPreset={this.state.selectedDuration}
                    appendTo="body"
                    disableCalendar={hideCalendar}
                    showWeeklyToggle={this.services.swSettings.current.resources.WeeklyGranularity === true}
                    isWeeklyDataAvailable={isWeeklyKeywordsAvailable(this.services.swSettings)}
                    weeklyToggleProps={this.getWeeklyTogglePropsForDurationPresetOriented()}
                    onGranularityToggleClick={this.openWeeklyKeywordsHookForDurationPresetOriented}
                    presentNotificationIndicator={isDurationSelectorIndicatorShown}
                    presentNotificationTooltipKey={getDurationSelectorTooltipKey()}
                    indicatorType={getIndicatorType()}
                />
            );

        return (
            <PeriodOverPeriodWrapper>
                {wrapWithTooltip ? (
                    <div
                        className="scss-tooltip scss-tooltip--s scss-tooltip--autohiding"
                        data-scss-tooltip={this.state.invalidDurationTooltipMessage}
                    >
                        {component}
                    </div>
                ) : (
                    component
                )}
            </PeriodOverPeriodWrapper>
        );
    }

    @autobind
    private getCompactFilters(): ISidebarListItem[] {
        const fixedFilters: ISidebarListItem[] = [];
        if (!this.props.removeSubDomainsFilter) {
            fixedFilters.push({
                listItem: (
                    <SubDomainsFilter
                        label="Include Subdomains"
                        key="filter-subdomains"
                        isSelected={this.state.includeSubDomains}
                        onClick={() => null}
                        disabledTooltip={this.state.includeSubDomainsDisabledTooltip}
                        isDisabled={
                            this.props.isDisabled ||
                            this.props.disableSubDomainsFilter ||
                            this.state.isSubDomainsFilterDisabled
                        }
                    />
                ),
                onSidebarListItemClick:
                    this.props.isDisabled || this.props.disableSubDomainsFilter || this.state.isSubDomainsFilterDisabled
                        ? undefined
                        : this.onSidebarListCompactElementItemChangeProp(null, this.onSubdomainsChange),
            });
        }
        if (this.state.isShowEstimationsVsGaToggle) {
            fixedFilters.push({
                listItem: (
                    <GaFilter
                        label={translate("analysis.ga.toggle.label")}
                        key="filter-ga"
                    />
                ),
            });
        }

        return fixedFilters;
    }

    @autobind
    private getFilterNotificationComponent() {
        return <FilterChangedNotification {...this.state.TooltipChangeProps} />;
    }

    @autobind
    private onCountryChange(country) {
        const currentCountry = this.services.swNavigator.getParams().country;
        if (currentCountry === country?.id) {
            return;
        }
        if (!this.state.skipFilterConsistency) {
            updateFilterConsistencyCountry(country?.id);
        }
        // BETA BRANCH ADDITION : for company analysis and website analysis T&E
        if (
            this.state.componentName ===
                this.services.swNavigator.getState("digitalsuite_website_trafficandengagement")?.configId &&
            this.props.showBetaBranchData.value &&
            !this.services.swSettings.components["TrafficAndEngagementBeta"]?.resources?.AllowedCountries.includes(
                country?.id,
            )
        ) {
            this.props.openResetBetaBranchModal(
                {
                    country: country?.id,
                },
                false,
            );
            return;
        }

        this.trackFilterValue(`Header/Country Filter/${country?.text}`);
        this.services.swNavigator.updateParams({
            country: country?.id,
            isCountryChanged: null,
        });
    }

    @autobind
    private onWebSourceChange(webSource) {
        const currentWebSource = this.services.swNavigator.getParams().webSource;
        if (!this.state.skipFilterConsistency) {
            this.props.webSourceSelected(webSource.id);
        }
        if (currentWebSource === webSource.id) {
            return;
        }
        this.trackFilterValue(`Header/WebSource Filter/${webSource.text}`);
        const currentState = this.services.swNavigator.current();
        const params = {
            webSource: webSource.id,
            ...(currentState.customData?.resetFiltersOnWebSourceChange ?? {}),
            isWebSourceChanged: null,
        };
        this.services.swNavigator.updateParams(params, { reload: true });
    }

    @autobind
    private onDurationChange(duration, comparedDuration) {
        this.trackFilterValue(`Date range/${duration}`, duration);
        if (!this.state.skipFilterConsistency) {
            this.props.durationSelected(duration);
        }
        const updateParams: {
            isWWW?: string;
            duration: string;
            comparedDuration?: string;
            isDurationChanged?: boolean;
        } = {
            duration,
            comparedDuration: comparedDuration ? comparedDuration : "",
            isDurationChanged: null,
        };

        const durationData = DurationService.getDurationData(duration);
        // SIM-29373: allow include subdomains for daily data only after 29.5.2020
        if (durationData.raw.isDaily && durationData.raw.from.isBefore(DAILY_DATA_INCLUDE_SUBDOMAINS_FROM)) {
            updateParams.isWWW = "-";
        }
        // BETA BRANCH ADDITION : for company analysis and website analysis T&E
        if (
            this.state.componentName ===
                this.services.swNavigator.getState("digitalsuite_website_trafficandengagement")?.configId &&
            this.props.showBetaBranchData.value &&
            (duration === "28d" ||
                comparedDuration ||
                durationData.raw.from.isBefore(
                    dayjs(this.services.swSettings.components["TrafficAndEngagementBeta"]?.resources?.StartDate),
                ))
        ) {
            this.props.openResetBetaBranchModal(updateParams);
            return;
        }

        this.services.swNavigator.updateParams(updateParams);
    }

    @autobind
    private onSubdomainsChange() {
        const subdomainsState = this.state.includeSubDomains;
        const trackValue = subdomainsState ? "main domain only" : "include subdomains";
        this.services.track.all.trackEvent("toggle", "switch", `filter side bar/include subdomains/${trackValue}`);
        this.services.swNavigator.updateParams({
            isWWW: subdomainsState ? "-" : "*",
        });
    }

    @autobind
    private getWebSources(state, params) {
        return utils.getAvailableWebSource(state, params);
    }

    @autobind
    private isCorrectState(toState) {
        return this.services.swNavigator.isWebsiteAnalysis(toState);
    }

    @autobind
    private onStateChangeSuccess(evt, toState, toParams) {
        if (!this.isCorrectState(toState)) {
            return;
        }
        const stateParams = this.getStateParams(toParams, toState);
        const periodOverPeriodDisabled = this.isPeriodOverPeriodDisabled(stateParams.comparedDurationItems);
        const countryFilterDisabled = this.isCountryDropDownDisabled(toState);
        const countryFilterAvailable = this.isCountryFilterAvailable(toState);
        const durationSelectorAvailable = this.isDurationSelectorAvailable(toState);
        const durationSelectorDisabled = this.isDurationSelectorDisabled(toParams, toState);
        const webSourceFilterAvailable = this.isWebSourceFilterAvailable(toState);
        const webSourceFilterDisabled = this.isWebSourceFilterDisabled(toParams, toState);
        const componentName = this.services.swNavigator.getConfigIdFromStateConfig(toState, toParams);
        // APPSP:1104 & https://github.com/reactwg/react-18/discussions/21
        // This was necessary in order to force the filter states to be updated
        //  before the filter components (Country, Duration and Websource) rendered.
        //  Otherwise, the filters retained there previous values despite the
        //  params and url having changed.
        flushSync(() => {
            this.setState({
                ...this.state,
                ...stateParams,
                keys: toParams.key,
                componentName,
                periodOverPeriodDisabled,
                countryFilterDisabled,
                countryFilterAvailable,
                durationSelectorDisabled,
                durationSelectorAvailable,
                webSourceFilterAvailable,
                webSourceFilterDisabled,
                comparedDurationAllowed: this.resolveCompareDurationAllowed(toParams, toState),
                mainWebsiteIsSubDomain: chosenSites.getPrimarySite().isSubDomain,
                durationPaymentHook: this.services.swSettings.current.durationPaymentHook,
                skipFilterConsistency: toState.skipFilterConsistency,
            });
        });

        this.updateGAToggleState(toParams.key);
    }

    @autobind
    private onStateChangeStart(event, to, toParams, from, fromParams, toConfigId) {
        if (!this.isCorrectState(to)) {
            return;
        }
        if (this.checkRedirectOnInvalidDurationWebsiteOverview(to, toParams)) {
            this.setState({
                ...this.state,
                showInvalidDurationTooltip: true,
                invalidDurationTooltipMessage: translate("wa.wwo.websource.tooltip"),
            });
        }
        if (this.checkRedirectOnInvalidDurationAudienceInterests(to, toParams)) {
            const latestValid = DurationService.getDurationData(to.overrideDatepickerPreset[0], undefined, toConfigId);
            const durationData = DurationService.getDurationData(toParams.duration, undefined, toConfigId);
            const closestPreset = DurationService.getClosestPreset(
                durationData.raw.from,
                latestValid.raw.to,
                to.overrideDatepickerPreset,
            );
            this.services.swNavigator.go(
                to,
                Object.assign(toParams, { duration: closestPreset ? closestPreset : "3m" }),
            );
            this.setState({
                ...this.state,
                showInvalidDurationTooltip: true,
                invalidDurationTooltipMessage: translate("wa.wwo.websource.tooltip"),
            });
        }
        if (this.checkRedirectOnInvalidDuration(toParams, fromParams, toConfigId)) {
            this.setState({
                ...this.state,
                showInvalidDurationTooltip: true,
                invalidDurationTooltipMessage: translate("duration.unavailable.tooltip", {
                    duration: DurationService.getDurationData(fromParams.duration, null, "WebAnalysis")
                        .forTitle as string,
                }),
            });
        }
    }

    private onNavUpdate = (transition) => {
        const to = transition.to();
        const toParams = transition.params();
        if (!this.isCorrectState(to)) {
            return;
        }
        const selectedCountry = _.find<{ id: number }>(this.state.availableCountries, {
            id: parseInt(toParams.country),
        });
        const selectedWebSource = _.find(this.state.availableWebsource, { id: toParams.webSource });
        this.setState({
            ...this.state,
            selectedCountry,
            selectedWebSource,
        });
    };

    @autobind
    private checkRedirectOnInvalidDurationWebsiteOverview(toState, toParams) {
        if (worldwideOverviewArray.includes(toState.name)) {
            if (
                toParams.country === "999" &&
                toParams.duration !== "3m" &&
                !this.services.swSettings.allowedCountry(toParams.country, "WebAnalysis")
            ) {
                return true;
            }
        }

        return false;
    }

    @autobind
    private checkRedirectOnInvalidDurationAudienceInterests(toState, toParams) {
        return (
            toState.name === "digitalsuite_website_audienceInterests" &&
            !swSettings.allowedDuration(toParams.duration, toState.configId)
        );
    }

    @autobind
    private checkRedirectOnInvalidDuration(toParams, fromParams, toConfigId) {
        // Fetch config object according to state's configId
        const previousDuration = fromParams.duration;
        const currentDuration = toParams.duration;
        const unsupportedDuration =
            previousDuration && !this.services.swSettings.allowedDuration(previousDuration, toConfigId);

        if (previousDuration !== currentDuration && unsupportedDuration) {
            return true;
        }
        return false;
    }

    private getComparedDurationDropDownItems(params) {
        return this.services.periodOverPeriod.getPeriodOverPeriodDropdownItems(
            params.duration,
            params.key,
            this.services.swSettings.current.componentId,
        );
    }

    private isPeriodOverPeriodDisabled(comparedDurationDropDownItems) {
        if (comparedDurationDropDownItems[0].disabled && comparedDurationDropDownItems[1].disabled) {
            return true;
        } else {
            return false;
        }
    }

    private isCountryDropDownDisabled(state) {
        return state.data?.filtersConfig?.country === FiltersEnum.DISABLED;
    }

    private isCountryFilterAvailable(state) {
        return state.data?.filtersConfig?.country !== FiltersEnum.HIDDEN;
    }

    private isDurationSelectorDisabled(params, state) {
        // For users that do not have 999 permissions in the Website overview page
        if (state.data?.filtersConfig?.duration === FiltersEnum.DISABLED) {
            return true;
        }
        return (
            worldwideOverviewArray.includes(state.name) &&
            params.country === "999" &&
            !this.services.swSettings.allowedCountry(params.country, "WebAnalysis")
        );
    }

    private resolveCompareDurationAllowed(params, stateConfig) {
        if (typeof stateConfig.periodOverPeriodEnabled === "function") {
            return stateConfig.periodOverPeriodEnabled(params);
        } else {
            return stateConfig.periodOverPeriodEnabled;
        }
    }

    private isDurationSelectorAvailable(state) {
        return state.data?.filtersConfig?.duration !== FiltersEnum.HIDDEN;
    }

    private isWebSourceFilterDisabled(params, state) {
        return (
            state.data?.filtersConfig?.webSource === FiltersEnum.DISABLED ||
            (worldwideOverviewArray.includes(state.name) &&
                params.country === "999" &&
                !this.services.swSettings.allowedCountry(params.country, "WebAnalysis"))
        );
    }

    private isWebSourceFilterAvailable(state) {
        return state.data?.filtersConfig?.webSource !== FiltersEnum.HIDDEN;
    }

    private getStateParams(params, state): IAppStateParams {
        const availableCountries = utils.getCountries(
            false,
            swSettings.current,
            state?.customData?.countryFilterInitialSortingType,
        );
        const availableWebsource = this.getWebSources(state, params);
        const isCountryFilterAllowData = state.customData?.isCountryFilterAllowData;
        const countryFilterSortingText = state.customData?.countryFilterSortingText;
        const countryFilterInitialSortingType = state.customData?.countryFilterInitialSortingType;
        const countryFilterPreferencesKey = state.customData?.countryFilterPreferencesKey;
        const isUSStatesSupported = state.isUSStatesSupported;
        let selectedCountry = _.find<{ id: number }>(availableCountries, {
            id: parseInt(params.country),
        });
        if (!selectedCountry) {
            if (swSettings.current.resources.InitialCountry) {
                selectedCountry = availableCountries.find(
                    ({ id }) => id === swSettings.current.resources.InitialCountry,
                );
            }
            if (!selectedCountry) {
                selectedCountry = _.get(availableCountries, 0, {});
            }
            this.services.swNavigator.updateParams({
                country: selectedCountry.id.toString(),
            });
        }
        const selectedWebSource = _.find(availableWebsource, { id: params.webSource });
        const selectedDuration = params.duration;
        const selectedComparedDuration = params.comparedDuration;
        const comparedDurationItems = this.getComparedDurationDropDownItems(params);

        return {
            isUSStatesSupported,
            countryFilterInitialSortingType,
            availableCountries,
            countryFilterSortingText,
            isCountryFilterAllowData,
            availableWebsource,
            selectedCountry,
            selectedWebSource,
            selectedDuration,
            selectedComparedDuration,
            includeSubDomains: params.isWWW === "*",
            minDate: this.services.swSettings.current.startDate,
            maxDate: this.services.swSettings.current.endDate,
            // minDailyDate: this.services.swSettings.current.windowStartDate,
            minDailyDate: this.services.swSettings.current.startDate,
            maxDailyDate: this.services.swSettings.current.windowEndDate,
            durationSelectorPresets: this.services.swSettings.current.datePickerPresets,
            durationSelectorWeeklyPresets: this.services.swSettings.current.weeklyDatePickerPresets,
            ...(this.props.removeSubDomainsFilter
                ? { isSubDomainsFilterDisabled: true, includeSubDomainsDisabledTooltip: null }
                : this.isSubdomainsFilterDisabled(params, state)),
            comparedDurationItems,
            countryFilterPreferencesKey,
        };
    }

    private isSubdomainsFilterDisabled = (params, state) => {
        const durationData = DurationService.getDurationData(params.duration);
        if (durationData.raw.isDaily && durationData.raw.from.isBefore(DAILY_DATA_INCLUDE_SUBDOMAINS_FROM)) {
            return {
                isSubDomainsFilterDisabled: true,
                includeSubDomainsDisabledTooltip: translate(
                    "analysis.trafficsource.search.subdomains.disabled.tooltip",
                ),
            };
        }
        return {
            isSubDomainsFilterDisabled: utils.isSubdomainsFilterDisabled(params, state),
            includeSubDomainsDisabledTooltip: null,
        };
    };

    private updateGAToggleState(domains) {
        connectedAssetsService
            .isShowEstimationsVsGaToggle(this.services.swNavigator.getApiParams().key)
            .then((isShowEstimationsVsGaToggle) => {
                this.setState({
                    ...this.state,
                    isShowEstimationsVsGaToggle,
                });
                if (!isShowEstimationsVsGaToggle) {
                    this.props.hideGAApprovedDataDispatch();
                }
            });
    }

    @autobind
    private onDurationCompactClick() {
        allTrackers.trackEvent("Drop Down", "open", `Date Range`);
    }

    @autobind
    private onSidebarListCompactElementItemChangeProp(item, cb) {
        return async () => {
            await this.setStateAsync({
                isSideBarOpen: false,
            });
            setTimeout(() => {
                cb(item);
            }, 400);
        };
    }

    @autobind
    private async onDurationChangeFromSideBar(x, y) {
        await this.setStateAsync({
            isSideBarOpen: false,
        });
        setTimeout(() => {
            this.onDurationChange(x, y);
        }, 400);
    }

    private getWeeklyCalendarLocked = () => {
        return (
            <WeeklyCalendarLocked
                title={translate("datepicker.granularity.weekly.hook.keywords.title")}
                subTitle={translate("datepicker.granularity.weekly.hook.keywords.subtitle")}
                text={translate("datepicker.granularity.weekly.hook.keywords.text")}
                buttonText={translate("datepicker.granularity.weekly.hook.keywords.button")}
                onButtonClick={this.openWeeklyKeywordsHook}
            />
        );
    };

    private openWeeklyKeywordsHookForDurationPresetOriented = (overrideTrackingKey) => {
        openHooksModalV5("WeeklyKeywords", overrideTrackingKey);
    };

    private getWeeklyTogglePropsForDurationPresetOriented = () => {
        return {
            weekly: (
                <StyledSwitcherItem alignItems="center">
                    {translate("datepicker.granularity.weekly")}
                    <SWReactIcons
                        iconName={"spark"}
                        size={"xs"}
                    />
                </StyledSwitcherItem>
            ),
            monthly: (
                <StyledSwitcherItem alignItems="center">
                    {translate("datepicker.granularity.monthly")}
                </StyledSwitcherItem>
            ),
        };
    };
    private openWeeklyKeywordsHook = () => {
        openHooksModalV5("WeeklyKeywords");
    };
    // CountryFilter consumes the open pulse itself; clearing on dismiss is a fallback
    // if the dropdown was opened from the filter button rather than the icon trigger.
    private onCountryFilterToggle = (isOpen: boolean) => {
        if (!isOpen) {
            this.props.closeCountryFilter();
        }
        this.trackFilterDropDownStatus("Header/Country Filter")(isOpen);
    };
}

function mapDispatchToProps(dispatch) {
    return {
        // update the GA toggle to false when the GA is unavailable
        hideGAApprovedDataDispatch: () => {
            dispatch(estimatedVsGaSwitchToggle(false));
        },
        openResetBetaBranchModal: (updateParams, useDurationTitle = true) => {
            dispatch(toggleResetBetaBranchModal(true, updateParams, useDurationTitle));
        },
        durationSelected: (duration) => {
            dispatch(durationFilterDSSelected(duration));
        },
        webSourceSelected: (webSource) => {
            dispatch(webSourceFilterDSSelected(webSource));
        },
        closeCountryFilter: hideCountryFilterHof(dispatch),
    };
}

function mapStateToProps({
    common: { showBetaBranchData },
    countryFilter,
    WebsiteAnalysisFilters: { countryFilter: isCountryFilterOpen },
}) {
    return {
        showBetaBranchData,
        countryFilter,
        isCountryFilterOpen,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withUIRouter(WebsiteAnalysisFilters));
